import { ref } from "vue";
import { defaultApi, type IResponse } from "@/api";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import type { UpdateWidgetRequest } from "@/features/control-tower/interfaces/update-widget";

export const useUpdateWidget = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);

  const updateWidget = async (widgetId: string, request: UpdateWidgetRequest): Promise<IResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Updating widget configuration for widget ID: ${widgetId}`);

    try {
      loading.value = true;
      error.value = null;

      const response = await defaultApi.apiVersion1.updateWidget.patchOne<IResponse>(widgetId, request);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Widget configuration updated successfully for widget ID: ${widgetId}`);

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to update widget configuration";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboard-widget/widgets/update",
        widgetId,
        error: errorMessage,
        statusCode: err?.response?.status
      });

      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error updating widget configuration: ${errorMessage}`);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboard-widget/widgets/update",
        widgetId
      });
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    updateWidget
  };
};
